import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ProductInstance } from "@/types/product";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ProductInstancesTableProps {
  instances: ProductInstance[];
}

type SortConfig = {
  key: keyof ProductInstance | "oprice" | "fprice";
  direction: "asc" | "desc";
};

export function ProductInstancesTable({
  instances,
}: ProductInstancesTableProps) {
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "title",
    direction: "asc",
  });

  const sortedInstances = [...instances].sort((a, b) => {
    if (sortConfig.key === "oprice") {
      const aPrice = a.current_price_details.oprice;
      const bPrice = b.current_price_details.oprice;
      return sortConfig.direction === "asc" ? aPrice - bPrice : bPrice - aPrice;
    }
    if (sortConfig.key === "fprice") {
      const aPrice = a.current_price_details.fprice;
      const bPrice = b.current_price_details.fprice;
      return sortConfig.direction === "asc" ? aPrice - bPrice : bPrice - aPrice;
    }
    if (sortConfig.key === "brand") {
      const aValue = a.brand.name.toLowerCase();
      const bValue = b.brand.name.toLowerCase();
      return sortConfig.direction === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    const aValue = String(a[sortConfig.key]).toLowerCase();
    const bValue = String(b[sortConfig.key]).toLowerCase();
    return sortConfig.direction === "asc"
      ? aValue.localeCompare(bValue)
      : bValue.localeCompare(aValue);
  });

  const requestSort = (key: SortConfig["key"]) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIcon = (columnKey: SortConfig["key"]) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }
    return sortConfig.direction === "asc" ? (
      <ArrowUp className="ml-2 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-2 h-4 w-4" />
    );
  };

  const handleProductClick = (url: string) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead
            onClick={() => requestSort("title")}
            className="cursor-pointer"
          >
            <Button
              variant="ghost"
              className={`p-0 h-4 ${
                sortConfig.key === "title" ? "text-primary font-semibold" : ""
              }`}
            >
              Title {getSortIcon("title")}
            </Button>
          </TableHead>
          <TableHead
            className="cursor-pointer"
          >
            Provider
          </TableHead>
          <TableHead
            onClick={() => requestSort("brand")}
            className="cursor-pointer"
          >
            <Button
              variant="ghost"
              className={`p-0 h-4 ${
                sortConfig.key === "brand" ? "text-primary font-semibold" : ""
              }`}
            >
              Brand {getSortIcon("brand")}
            </Button>
          </TableHead>
          <TableHead
            onClick={() => requestSort("oprice")}
            className="cursor-pointer"
          >
            <Button
              variant="ghost"
              className={`p-0 h-4 ${
                sortConfig.key === "oprice" ? "text-primary font-semibold" : ""
              }`}
            >
              Original Price {getSortIcon("oprice")}
            </Button>
          </TableHead>
          <TableHead
            onClick={() => requestSort("fprice")}
            className="cursor-pointer"
          >
            <Button
              variant="ghost"
              className={`p-0 h-4 ${
                sortConfig.key === "fprice" ? "text-primary font-semibold" : ""
              }`}
            >
              Final Price {getSortIcon("fprice")}
            </Button>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedInstances.map((instance, index) => (
          <TableRow key={index}>
            <TableCell>
              <Button
                variant="link"
                onClick={() => handleProductClick(instance?.url || "")}
              >
                {instance.title}
              </Button>
            </TableCell>
            <TableCell>{instance.provider.name}</TableCell>
            <TableCell>{instance.brand.name}</TableCell>
            <TableCell>{instance.current_price_details.oprice}</TableCell>
            <TableCell>{instance.current_price_details.fprice}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
